import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { interestsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Heart, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const positions = [
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const operationTypes = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'both', label: 'Ambos (Transferencia o Préstamo)' },
];

const urgencies = [
  { value: 'high', label: 'Alta - Lo necesitamos ya' },
  { value: 'medium', label: 'Media - Para esta temporada' },
  { value: 'low', label: 'Baja - A futuro' },
];

export const AddInterestPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    player_name: '',
    player_current_club: '',
    position: '',
    operation_type: '',
    budget: '',
    max_salary: '',
    urgency: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.player_name || !formData.position || !formData.operation_type || !formData.urgency) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        max_salary: formData.max_salary ? parseFloat(formData.max_salary) : null,
      };
      
      await interestsAPI.create(data);
      toast.success('Interés enviado correctamente. Solo Cracks se pondrá en contacto contigo.');
      navigate('/my-publications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el interés');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="add-interest-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start gap-4 mb-6 pb-6 border-b border-slate-200">
            <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center">
              <Heart className="w-7 h-7 text-pink-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 uppercase tracking-tight">
                Indicar Interés en Jugadora
              </h1>
              <p className="text-slate-500 mt-1">
                ¿Te interesa una jugadora que representa Solo Cracks? Indícanos quién es y nos pondremos en contacto contigo.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Player info */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Información de la Jugadora *
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="form-label">Nombre de la jugadora *</Label>
                  <Input
                    value={formData.player_name}
                    onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                    placeholder="Nombre completo de la jugadora"
                    required
                    data-testid="interest-player-name"
                  />
                </div>

                <div>
                  <Label className="form-label">Club actual (si lo conoces)</Label>
                  <Input
                    value={formData.player_current_club}
                    onChange={(e) => setFormData({ ...formData, player_current_club: e.target.value })}
                    placeholder="Nombre del club actual"
                    data-testid="interest-player-club"
                  />
                </div>

                <div>
                  <Label className="form-label">Posición *</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData({ ...formData, position: value })}
                    required
                  >
                    <SelectTrigger data-testid="interest-position">
                      <SelectValue placeholder="Seleccionar posición" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Operation details */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Detalles de la Operación
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Tipo de operación *</Label>
                  <Select
                    value={formData.operation_type}
                    onValueChange={(value) => setFormData({ ...formData, operation_type: value })}
                    required
                  >
                    <SelectTrigger data-testid="interest-type">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {operationTypes.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Urgencia *</Label>
                  <Select
                    value={formData.urgency}
                    onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                    required
                  >
                    <SelectTrigger data-testid="interest-urgency">
                      <SelectValue placeholder="Seleccionar urgencia" />
                    </SelectTrigger>
                    <SelectContent>
                      {urgencies.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Presupuesto aproximado (€) <span className="text-slate-400 font-normal normal-case">- opcional</span></Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="A negociar"
                    data-testid="interest-budget"
                  />
                </div>

                <div>
                  <Label className="form-label">Salario máximo (€/año) <span className="text-slate-400 font-normal normal-case">- opcional</span></Label>
                  <Input
                    type="number"
                    value={formData.max_salary}
                    onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                    placeholder="A negociar"
                    data-testid="interest-salary"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Notas adicionales</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="¿Por qué te interesa esta jugadora? ¿Algún requisito específico?"
                    rows={4}
                    data-testid="interest-notes"
                  />
                </div>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-indigo-800">¿Cómo funciona?</p>
                  <p className="text-sm text-indigo-600 mt-1">
                    Una vez envíes tu interés, el equipo de Solo Cracks lo revisará y se pondrá en contacto contigo 
                    para informarte sobre la disponibilidad de la jugadora y las condiciones.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-pink-600 hover:bg-pink-700"
                disabled={loading}
                data-testid="submit-interest"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-2" />
                    Enviar Interés
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddInterestPage;
